import { Box, Button, Typography } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { useDocument_DeleteFilesMutation } from 'queries';

import Modal from 'common/modal';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

type Props = {
  showModal: boolean;
  portalId: string;
  setShowModal: (show: boolean) => void;
  documentIds: string[];
  onDeleteSuccess: () => void;
};

const useStyles = makeStyles(() =>
  createStyles({
    container: {
      color: '#1C2541',
      paddingTop: '20px',
    },
    title: {
      fontWeight: 700,
      fontSize: '20px',
      marginBottom: '32px',
    },
    subtitle: {
      fontSize: '14px',
      color: '#596674',
    },
    cancelButton: {
      borderRadius: '100px',
      maxHeight: '40px',
      marginTop: '40px',
      marginBottom: '20px',
      backgroundColor: '#5ED1F0',
    },
    removeButton: {
      borderRadius: '100px',
      color: '#9E2B25',
      borderColor: '#9E2B25',
      marginBottom: '10px',
    },
    subContainer: {
      display: 'flex',
      flexDirection: 'column',
    },
  }),
);

export default function DocumentDeleteModal({
  documentIds,
  portalId,
  setShowModal,
  showModal,
  onDeleteSuccess,
}: Props) {
  const classes = useStyles();
  const [deleteFile, { loading: deleteFileLoading }] = useDocument_DeleteFilesMutation({
    variables: {
      ids: documentIds,
      portalId,
    },
    onCompleted: () => onDeleteSuccess(),
    onError: (e) =>
      showAlert({
        message: 'Error deleting files: ' + e.message,
        severity: NotificationSeverity.ERROR,
      }),
  });

  const renderContent = () => {
    return (
      <Box className={classes.container}>
        <Typography className={classes.title}>
          Are you sure you want to remove this file?
        </Typography>
        <Typography className={classes.subtitle}>This action can't be undone.</Typography>
        <Box className={classes.subContainer}>
          <Button
            onClick={() => setShowModal(false)}
            variant="contained"
            className={classes.cancelButton}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            component="span"
            onClick={handleDelete}
            variant="outlined"
            className={classes.removeButton}
          >
            Remove file
          </Button>
        </Box>
      </Box>
    );
  };

  const handleDelete = async () => {
    await deleteFile();
  };

  return (
    <Modal
      setShowModal={setShowModal}
      showModal={showModal}
      onExitClick={() => setShowModal(false)}
      content={renderContent()}
      actionVerb="Delete"
      showActions={false}
      loading={deleteFileLoading}
    />
  );
}
